import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';

interface CampaignPerformance {
  id: string;
  name: string;
  channel: Channel;
  dateRange: { start: Date; end: Date };
  metrics: PerformanceMetrics;
  funnel: FunnelData[];
  attribution: AttributionData[];
  cohort: CohortData[];
  trends: TrendData[];
}

interface Channel {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'line' | 'web' | 'store' | 'social' | 'all';
  icon: string;
}

interface PerformanceMetrics {
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  cvr: number;
  revenue: number;
  cost: number;
  roi: number;
  cpa: number;
  ltv: number;
}

interface FunnelData {
  stage: string;
  value: number;
  dropoffRate?: number;
}

interface AttributionData {
  touchpoint: string;
  attribution: number;
  conversions: number;
  revenue: number;
}

interface CohortData {
  cohort: string;
  retention: number[];
  revenue: number[];
}

interface TrendData {
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
}

interface PerformanceAnalyticsProps {
  campaignId?: string;
  period?: 'daily' | 'weekly' | 'monthly';
  onMetricClick?: (metric: string, value: any) => void;
}

const PerformanceAnalytics: React.FC<PerformanceAnalyticsProps> = ({
  campaignId = 'campaign001',
  period = 'daily',
  onMetricClick
}) => {
  const [performance, setPerformance] = useState<CampaignPerformance | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'overview' | 'funnel' | 'attribution' | 'cohort'>('overview');
  const [compareMode, setCompareMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // チャネル定義
  const channels: Channel[] = [
    { id: 'all', name: 'すべて', type: 'all', icon: '📊' },
    { id: 'email', name: 'メール', type: 'email', icon: '📧' },
    { id: 'line', name: 'LINE', type: 'line', icon: '💚' },
    { id: 'web', name: 'Web広告', type: 'web', icon: '🌐' },
    { id: 'store', name: '店頭', type: 'store', icon: '🏪' },
    { id: 'social', name: 'SNS', type: 'social', icon: '📱' }
  ];

  // サンプルパフォーマンスデータ生成
  const generatePerformanceData = (): CampaignPerformance => {
    return {
      id: campaignId,
      name: '春の大感謝祭キャンペーン',
      channel: channels.find(c => c.id === selectedChannel) || channels[0],
      dateRange: {
        start: new Date('2025-03-01'),
        end: new Date('2025-03-31')
      },
      metrics: {
        impressions: 250000,
        clicks: 12500,
        ctr: 5.0,
        conversions: 1875,
        cvr: 15.0,
        revenue: 5625000,
        cost: 1125000,
        roi: 400,
        cpa: 600,
        ltv: 15000
      },
      funnel: [
        { stage: '広告表示', value: 250000 },
        { stage: 'クリック', value: 12500, dropoffRate: 95 },
        { stage: 'サイト訪問', value: 10000, dropoffRate: 20 },
        { stage: 'カート追加', value: 3750, dropoffRate: 62.5 },
        { stage: '購入完了', value: 1875, dropoffRate: 50 }
      ],
      attribution: [
        { touchpoint: '初回広告', attribution: 35, conversions: 656, revenue: 1968750 },
        { touchpoint: 'メール', attribution: 25, conversions: 469, revenue: 1406250 },
        { touchpoint: 'リターゲティング', attribution: 20, conversions: 375, revenue: 1125000 },
        { touchpoint: 'SNS', attribution: 15, conversions: 281, revenue: 843750 },
        { touchpoint: '直接流入', attribution: 5, conversions: 94, revenue: 281250 }
      ],
      cohort: [
        { cohort: '第1週', retention: [100, 45, 28, 15], revenue: [1500000, 675000, 420000, 225000] },
        { cohort: '第2週', retention: [100, 48, 30], revenue: [1600000, 768000, 480000] },
        { cohort: '第3週', retention: [100, 50], revenue: [1700000, 850000] },
        { cohort: '第4週', retention: [100], revenue: [1825000] }
      ],
      trends: [
        { date: '3/1', impressions: 8000, clicks: 400, conversions: 60, revenue: 180000 },
        { date: '3/5', impressions: 9000, clicks: 450, conversions: 68, revenue: 203000 },
        { date: '3/10', impressions: 8500, clicks: 425, conversions: 64, revenue: 191000 },
        { date: '3/15', impressions: 10000, clicks: 500, conversions: 75, revenue: 225000 },
        { date: '3/20', impressions: 9500, clicks: 475, conversions: 71, revenue: 213000 },
        { date: '3/25', impressions: 11000, clicks: 550, conversions: 83, revenue: 248000 },
        { date: '3/31', impressions: 8800, clicks: 440, conversions: 66, revenue: 198000 }
      ]
    };
  };

  useEffect(() => {
    setTimeout(() => {
      setPerformance(generatePerformanceData());
      setLoading(false);
    }, 600);
  }, [campaignId, period, selectedChannel]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  const renderMetricCard = (
    title: string,
    value: string | number,
    change?: number,
    format?: 'number' | 'currency' | 'percent',
    color?: string
  ) => (
    <div
      onClick={() => onMetricClick?.(title, value)}
      style={{
        padding: spacing.sm,
        backgroundColor: colors.background.primary,
        border: `1px solid ${colors.support.gray}`,
        borderRadius: borderRadius.sm,
        cursor: 'pointer',
        transition: 'all 200ms ease'
      }}
    >
      <div style={{ fontSize: '0.625rem', color: colors.text.light, marginBottom: '2px' }}>
        {title}
      </div>
      <div style={{ 
        fontSize: '0.875rem', 
        fontWeight: 600, 
        color: color || colors.text.dark,
        marginBottom: change !== undefined ? '2px' : 0
      }}>
        {format === 'currency' ? formatCurrency(value as number) :
         format === 'percent' ? `${value}%` :
         typeof value === 'number' ? formatNumber(value) : value}
      </div>
      {change !== undefined && (
        <div style={{ 
          fontSize: '0.625rem',
          color: change >= 0 ? colors.status.success : colors.status.error,
          display: 'flex',
          alignItems: 'center',
          gap: '1px'
        }}>
          <span style={{ fontSize: '0.5rem' }}>{change >= 0 ? '↑' : '↓'}</span>
          <span>{Math.abs(change)}%</span>
        </div>
      )}
    </div>
  );

  const renderFunnel = () => {
    if (!performance) return null;
    const maxValue = Math.max(...performance.funnel.map(f => f.value));
    
    return (
      <div style={{ padding: spacing.lg }}>
        <h3 style={{ 
          fontSize: '1.25rem',
          fontWeight: 600,
          color: colors.text.dark,
          marginBottom: spacing.lg
        }}>
          コンバージョンファネル
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {performance.funnel.map((stage, index) => {
            const width = (stage.value / maxValue) * 100;
            return (
              <div key={stage.stage}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: spacing.xs,
                  fontSize: '0.875rem'
                }}>
                  <span style={{ color: colors.text.dark }}>{stage.stage}</span>
                  <div style={{ display: 'flex', gap: spacing.md }}>
                    <span style={{ fontWeight: 600 }}>{formatNumber(stage.value)}</span>
                    {stage.dropoffRate && (
                      <span style={{ color: colors.status.error }}>
                        -{ stage.dropoffRate}%
                      </span>
                    )}
                  </div>
                </div>
                <div style={{
                  position: 'relative',
                  height: '40px',
                  backgroundColor: colors.support.gray,
                  borderRadius: borderRadius.sm,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${width}%`,
                    background: `linear-gradient(90deg, 
                      ${agentThemes.marketing.primary} 0%, 
                      ${colors.accent.blue} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: spacing.sm,
                    transition: 'width 500ms ease'
                  }}>
                    <span style={{ 
                      color: '#FFFFFF',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}>
                      {((stage.value / performance.funnel[0].value) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAttribution = () => {
    if (!performance) return null;
    
    return (
      <div style={{ padding: spacing.lg }}>
        <h3 style={{ 
          fontSize: '1.25rem',
          fontWeight: 600,
          color: colors.text.dark,
          marginBottom: spacing.lg
        }}>
          アトリビューション分析
        </h3>
        <div style={{ display: 'grid', gap: spacing.md }}>
          {performance.attribution.map(attr => (
            <div key={attr.touchpoint} style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.md,
              padding: spacing.md,
              backgroundColor: colors.background.secondary,
              borderRadius: borderRadius.md
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: colors.text.dark,
                  marginBottom: spacing.xs
                }}>
                  {attr.touchpoint}
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                  {attr.conversions}件のコンバージョン
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ 
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: agentThemes.marketing.primary
                }}>
                  {attr.attribution}%
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                  {formatCurrency(attr.revenue)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTrends = () => {
    if (!performance) return null;
    const maxRevenue = Math.max(...performance.trends.map(t => t.revenue));
    
    return (
      <div style={{ padding: spacing.lg }}>
        <h3 style={{ 
          fontSize: '1.25rem',
          fontWeight: 600,
          color: colors.text.dark,
          marginBottom: spacing.lg
        }}>
          パフォーマンストレンド
        </h3>
        <div style={{ 
          height: '250px',
          position: 'relative',
          marginBottom: spacing.lg
        }}>
          <svg width="100%" height="100%" viewBox="0 0 600 200">
            {/* Grid */}
            {[0, 1, 2, 3, 4].map(i => (
              <line
                key={i}
                x1="50"
                y1={40 + i * 30}
                x2="550"
                y2={40 + i * 30}
                stroke={colors.support.gray}
                strokeWidth="1"
                strokeDasharray={i === 4 ? "0" : "2,2"}
              />
            ))}
            
            {/* Revenue line */}
            <polyline
              points={performance.trends.map((data, index) => {
                const x = 50 + (index * 500) / (performance.trends.length - 1);
                const y = 160 - (data.revenue / maxRevenue) * 120;
                return `${x},${y}`;
              }).join(' ')}
              stroke={agentThemes.marketing.primary}
              strokeWidth="2"
              fill="none"
            />
            
            {/* Data points */}
            {performance.trends.map((data, index) => {
              const x = 50 + (index * 500) / (performance.trends.length - 1);
              const y = 160 - (data.revenue / maxRevenue) * 120;
              return (
                <g key={index}>
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    fill={agentThemes.marketing.primary}
                  />
                  <text
                    x={x}
                    y="185"
                    fontSize="10"
                    fill={colors.text.light}
                    textAnchor="middle"
                  >
                    {data.date}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* メトリクステーブル */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.support.gray}` }}>
                <th style={{ 
                  padding: spacing.sm,
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: colors.text.secondary
                }}>
                  日付
                </th>
                <th style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary }}>
                  インプレッション
                </th>
                <th style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary }}>
                  クリック
                </th>
                <th style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary }}>
                  コンバージョン
                </th>
                <th style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: colors.text.secondary }}>
                  売上
                </th>
              </tr>
            </thead>
            <tbody>
              {performance.trends.map((trend, index) => (
                <tr key={index} style={{ borderBottom: `1px solid ${colors.support.gray}` }}>
                  <td style={{ padding: spacing.sm, fontSize: '0.875rem' }}>{trend.date}</td>
                  <td style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.875rem' }}>
                    {formatNumber(trend.impressions)}
                  </td>
                  <td style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.875rem' }}>
                    {formatNumber(trend.clicks)}
                  </td>
                  <td style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.875rem' }}>
                    {trend.conversions}
                  </td>
                  <td style={{ padding: spacing.sm, textAlign: 'right', fontSize: '0.875rem', fontWeight: 600 }}>
                    {formatCurrency(trend.revenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: spacing.lg }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 600, 
            color: colors.text.dark,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <span>{agentThemes.marketing.icon}</span>
            パフォーマンス分析
          </h2>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <Select
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
              style={{ width: '150px' }}
            >
              {channels.map(channel => (
                <option key={channel.id} value={channel.id}>
                  {channel.name}
                </option>
              ))}
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCompareMode(!compareMode)}
              style={{
                backgroundColor: compareMode ? agentThemes.marketing.secondary : colors.background.primary
              }}
            >
              比較モード
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {/* エクスポート処理 */}}
            >
              レポート出力
            </Button>
          </div>
        </div>

        {/* ビューモード切り替え */}
        <div style={{ display: 'flex', gap: spacing.sm }}>
          {(['overview', 'funnel', 'attribution', 'cohort'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: `${spacing.sm} ${spacing.md}`,
                borderRadius: borderRadius.sm,
                border: 'none',
                backgroundColor: viewMode === mode ? agentThemes.marketing.primary : colors.support.gray,
                color: viewMode === mode ? '#FFFFFF' : colors.text.dark,
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                transition: 'all 200ms ease'
              }}
            >
              {mode === 'overview' && '概要'}
              {mode === 'funnel' && 'ファネル'}
              {mode === 'attribution' && 'アトリビューション'}
              {mode === 'cohort' && 'コホート'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: colors.text.light }}>データを分析中...</div>
        </div>
      ) : (
        <>
          {viewMode === 'overview' && (
            <>
              {/* KPIカード */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: spacing.md,
                marginBottom: spacing.lg
              }}>
                {performance && (
                  <>
                    {renderMetricCard('インプレッション', performance.metrics.impressions, 12.5, 'number')}
                    {renderMetricCard('クリック数', performance.metrics.clicks, 8.3, 'number')}
                    {renderMetricCard('CTR', performance.metrics.ctr, 5.2, 'percent', colors.accent.blue)}
                    {renderMetricCard('コンバージョン', performance.metrics.conversions, 15.8, 'number')}
                    {renderMetricCard('CVR', performance.metrics.cvr, 10.5, 'percent', colors.status.success)}
                    {renderMetricCard('売上', performance.metrics.revenue, 22.3, 'currency', agentThemes.marketing.primary)}
                    {renderMetricCard('コスト', performance.metrics.cost, -5.2, 'currency')}
                    {renderMetricCard('ROI', performance.metrics.roi, 18.7, 'percent', colors.status.success)}
                    {renderMetricCard('CPA', performance.metrics.cpa, -8.4, 'currency')}
                    {renderMetricCard('LTV', performance.metrics.ltv, 12.1, 'currency')}
                  </>
                )}
              </div>

              {/* トレンドグラフ */}
              <Card>
                {renderTrends()}
              </Card>
            </>
          )}

          {viewMode === 'funnel' && (
            <Card>
              {renderFunnel()}
            </Card>
          )}

          {viewMode === 'attribution' && (
            <Card>
              {renderAttribution()}
            </Card>
          )}

          {viewMode === 'cohort' && performance && (
            <Card>
              <div style={{ padding: spacing.lg }}>
                <h3 style={{ 
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: colors.text.dark,
                  marginBottom: spacing.lg
                }}>
                  コホート分析
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.support.gray}` }}>
                        <th style={{ 
                          padding: spacing.sm,
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: colors.text.dark
                        }}>
                          コホート
                        </th>
                        <th style={{ padding: spacing.sm, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>
                          第1週
                        </th>
                        <th style={{ padding: spacing.sm, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>
                          第2週
                        </th>
                        <th style={{ padding: spacing.sm, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>
                          第3週
                        </th>
                        <th style={{ padding: spacing.sm, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>
                          第4週
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {performance.cohort.map((cohort, index) => (
                        <tr key={index} style={{ borderBottom: `1px solid ${colors.support.gray}` }}>
                          <td style={{ 
                            padding: spacing.sm,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}>
                            {cohort.cohort}
                          </td>
                          {[0, 1, 2, 3].map(week => (
                            <td key={week} style={{ 
                              padding: spacing.sm,
                              textAlign: 'center',
                              fontSize: '0.875rem'
                            }}>
                              {cohort.retention[week] !== undefined ? (
                                <div>
                                  <div style={{
                                    fontWeight: 600,
                                    color: cohort.retention[week] > 30 
                                      ? colors.status.success 
                                      : cohort.retention[week] > 15 
                                      ? colors.accent.blue
                                      : colors.text.dark
                                  }}>
                                    {cohort.retention[week]}%
                                  </div>
                                  {cohort.revenue[week] && (
                                    <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                                      {formatCurrency(cohort.revenue[week])}
                                    </div>
                                  )}
                                </div>
                              ) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PerformanceAnalytics;